import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { Bars } from '@/components/charts';
import { Timeline } from '@/components/Timeline';
import { Card, HBars, Kpi, Loaded, LOGIN_NEEDED, NotSynced, RangeSeg } from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { countDelta, fmtAgo, fmtDay, fmtInt, fmtMoney, fmtPct, initialsOf, rateDelta } from '@/lib/format';
import { feedHref, RANGES, useParam } from '@/lib/params';
import type { CompanyProfile } from '@/lib/types';

const TABS = [
  { key: 'timeline', label: 'Timeline' },
  { key: 'asks', label: 'Asks' },
  { key: 'failures', label: 'Failures' },
  { key: 'features', label: 'Features' },
  { key: 'cost', label: 'Cost' },
];

function Profile({ p }: { p: CompanyProfile }) {
  const meta = useMeta();
  const [range] = useParam('range', '30d');
  const [tabParam, setTab] = useParam('tab', 'timeline');
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : 'timeline';
  const c = p.company;
  const k = p.kpis;
  const now = meta.data?.now ?? new Date().toISOString();
  const name = c.name ?? `Company #${c.id}`;
  const feedBase = { company_id: String(c.id), customers: 'false', range };
  const paceNote =
    k.spend.budget_share === null
      ? 'no AI budget'
      : `${fmtPct(k.spend.budget_share)} of this month's budget · ${k.spend.budget_share > k.spend.pace + 0.1 ? 'ahead of pace' : 'on pace'}`;
  const usedAreas = p.areas.filter((a) => a.asks > 0).length;
  return (
    <>
      <div className="ph">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'var(--brand-soft)',
              color: 'var(--brand)',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            {initialsOf(name).slice(0, 1)}
          </div>
          <div>
            <div className="h1">
              {name}
              {!c.is_customer && (
                <span className="pill neu" style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                  not a customer
                </span>
              )}
            </div>
            <div className="muted">
              {[
                c.plan_key,
                `company #${c.id}`,
                c.organization_name,
                c.created_at && `customer since ${format(parseISO(c.created_at), 'd MMM yyyy')}`,
                c.status,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </div>
        <div className="filters">
          <button type="button" className="btn" disabled title="Opening a customer's company needs staff access: later">
            Open in Compass ↗
          </button>
          <Link className="btn" to={feedHref({ ...feedBase, show: 'asks,mcp,actions,system' })}>
            Feed for this company
          </Link>
          <RangeSeg options={RANGES.standard} fallback="30d" />
        </div>
      </div>

      <div className="kpis">
        <Kpi
          label="Asks"
          value={fmtInt(k.asks.value)}
          delta={countDelta(k.asks.value, k.asks.prev)}
          spark={k.asks.spark}
        />
        <Kpi
          label="Active users"
          value={
            <>
              {fmtInt(k.users.active)}
              <span className="faint" style={{ fontSize: 16 }}>
                {' '}
                / {fmtInt(k.users.total)}
              </span>
            </>
          }
          footer={<span className="d muted">{k.users.never_asked} never asked AI</span>}
        />
        <Kpi
          label="Not-ok rate"
          value={fmtPct(k.not_ok_rate.value)}
          delta={rateDelta(k.not_ok_rate.value, k.not_ok_rate.prev)}
          spark={k.not_ok_rate.spark}
          sparkBad
        />
        <Kpi label="AI spend" value={fmtMoney(k.spend.value)} footer={<span className="d muted">{paceNote}</span>} />
        <Kpi
          label="Last active"
          value={<span style={{ fontSize: 22 }}>{k.last_active ? `${fmtAgo(k.last_active.at, now)} ago` : '–'}</span>}
          footer={
            <span className="d muted">
              {[k.last_active?.user, k.last_active?.area_label].filter(Boolean).join(' · ')}
            </span>
          }
        />
      </div>

      <div className="prof">
        <div className="card">
          <div className="ptabs">
            {TABS.map((t) => (
              <span key={t.key} role="button" className={cx(t.key === tab && 'on')} onClick={() => setTab(t.key)}>
                {t.key === 'asks' ? `Asks ${fmtInt(k.asks.value)}` : t.label}
              </span>
            ))}
          </div>
          {tab === 'timeline' && <Timeline params={{ ...feedBase, show: 'asks,mcp,actions,system' }} />}
          {tab === 'asks' && <Timeline params={{ ...feedBase, show: 'asks,mcp' }} />}
          {tab === 'failures' && <Timeline params={{ ...feedBase, show: 'asks,mcp,system', outcome: 'not_ok' }} />}
          {tab === 'features' &&
            (p.features.length === 0 ? (
              <div className="muted">No AI use in this range.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Area</th>
                    <th className="num">Asks</th>
                    <th className="num">Not-ok</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {p.features.map((f) => (
                    <tr key={f.key}>
                      <td>
                        <b>{f.label}</b>
                      </td>
                      <td className="num">{fmtInt(f.asks)}</td>
                      <td className="num">{fmtPct(f.not_ok_rate)}</td>
                      <td className="num">{fmtMoney(f.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          {tab === 'cost' && (
            <>
              <Bars
                labels={p.cost.days.map(fmtDay)}
                values={p.cost.per_day}
                color="var(--brand-2)"
                format={fmtMoney}
                title={(i) => fmtDay(p.cost.days[i])}
                unit="spent"
              />
              <HBars
                rows={p.cost.by_area.map((a) => ({ key: a.key, label: a.label, value: a.cost }))}
                format={fmtMoney}
              />
            </>
          )}
        </div>

        <div className="stack3">
          <Card
            title="Users"
            right={
              <span className="faint" style={{ fontSize: 12 }}>
                asks · last seen
              </span>
            }
          >
            {p.users.map((u) => (
              <Link key={u.id} to={`/users/${u.id}`} className="urow">
                <span className="uav">{initialsOf(u.name ?? u.email)}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <b>{u.name ?? u.email}</b>
                  {u.is_internal && (
                    <span className="tag" style={{ marginLeft: 6 }}>
                      staff
                    </span>
                  )}
                  <div className="faint" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {u.email}
                  </div>
                </div>
                <div className="faint" style={{ fontSize: 12, textAlign: 'right' }}>
                  {fmtInt(u.asks)}
                  <br />
                  {fmtAgo(u.last_seen_at, now)}
                </div>
              </Link>
            ))}
          </Card>
          <Card
            title="Features used"
            right={
              <span className="faint" style={{ fontSize: 12 }}>
                {usedAreas} of {p.areas.length}
              </span>
            }
          >
            <div className="agrid">
              {p.areas.map((a) => (
                <div key={a.key} className={cx(a.asks > 0 && 'u')}>
                  <b>{a.asks > 0 ? fmtInt(a.asks) : <span className="faint">–</span>}</b>
                  {a.short}
                </div>
              ))}
            </div>
          </Card>
          <Card title="Data health">
            <NotSynced what="Data health" />
          </Card>
          <Card
            title="Team notes"
            right={
              <span className="link disabled" title={LOGIN_NEEDED}>
                + Add
              </span>
            }
          >
            <div className="muted" style={{ fontSize: 12 }}>
              Notes arrive with login.
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

export function CompanyPage() {
  const { companyId } = useParams();
  const [range] = useParam('range', '30d');
  const query = useApi<CompanyProfile>(`/accounts/${companyId}`, { range });
  return (
    <div className="page">
      <div className="muted" style={{ marginBottom: 10 }}>
        <Link className="link" to="/accounts">
          Accounts
        </Link>{' '}
        / {query.data?.company.name ?? `#${companyId}`}
      </div>
      <Loaded query={query}>{(p) => <Profile p={p} />}</Loaded>
    </div>
  );
}
